/**
 * The address book lives in `shared/` and not in a feature (contract v10.44).
 *
 * Two features need it: the mail manager's form, which suggests while «Para» is typed, and
 * Administración → Contactos, which keeps it. A feature may import from `shared/` and never
 * from another feature, so the calls and their keys are here.
 */
import { api, type Page } from '@/shared/api/client';

import type {
  Contact,
  ContactChange,
  ContactGroup,
  ContactGroupWrite,
  ContactSuggestion,
  ContactWrite,
} from './types';

export interface ContactListParams {
  search?: string;
  limit?: number;
  offset?: number;
}

/** One page of Administración → Contactos. */
export const CONTACTS_PAGE = 50;

export const contactKeys = {
  /** Everything of the address book: the suggestions, the lists and the groups. A group
   * written changes what «Para» suggests, so a write invalidates the root. */
  all: ['contacts'] as const,
  suggest: (q: string, mailbox: string) => ['contacts', 'suggest', q, mailbox] as const,
  lists: ['contacts', 'list'] as const,
  list: (params: ContactListParams) => ['contacts', 'list', params] as const,
  groups: ['contacts', 'groups'] as const,
};

export const contactsApi = {
  /**
   * What «Para», «Copia» and «Copia oculta» offer while typing: the groups whose name
   * starts with what was typed first, then the contacts, the most written to from
   * `mailbox` first. A mailbox the person does not see is ignored by the back.
   */
  suggest: (q: string, mailbox: string): Promise<ContactSuggestion[]> =>
    api.get<ContactSuggestion[]>('/api/contacts/suggest', {
      q,
      ...(mailbox ? { mailbox } : {}),
      limit: 8,
    }),

  list: (params: ContactListParams = {}): Promise<Page<Contact>> =>
    api.get<Page<Contact>>('/api/contacts', { limit: CONTACTS_PAGE, ...params }),
  /** Whoever administers; no elevated session. 409 when the address was already added. */
  create: (payload: ContactWrite): Promise<Contact> => api.post<Contact>('/api/contacts', payload),
  /** Only what is sent changes. */
  update: (id: string, payload: ContactChange): Promise<Contact> =>
    api.patch<Contact>(`/api/contacts/${id}`, payload),
  remove: (id: string): Promise<void> => api.delete<void>(`/api/contacts/${id}`),

  /** Every group with its addresses; «Comerciales» always, and first. */
  groups: (): Promise<ContactGroup[]> => api.get<ContactGroup[]>('/api/contact-groups'),
  /** The writes of a group ask for the elevated session (403 `elevation_required`). */
  createGroup: (payload: ContactGroupWrite): Promise<ContactGroup> =>
    api.post<ContactGroup>('/api/contact-groups', payload),
  updateGroup: (id: string, payload: ContactGroupWrite): Promise<ContactGroup> =>
    api.put<ContactGroup>(`/api/contact-groups/${id}`, payload),
  removeGroup: (id: string): Promise<void> => api.delete<void>(`/api/contact-groups/${id}`),
};
